import type { BehaviorDocument, BTNode } from "../types";
import type { SceneBehaviorContext } from "./buildSceneBehaviorContext";
import { buildSceneBehaviorContext } from "./buildSceneBehaviorContext";
import { GO2_WALK_TEMPLATE_ID } from "../templates/go2Walk";
import { SPOT_WALK_TEMPLATE_ID } from "../templates/spotWalk";
import { PICK_PLACE_TEMPLATE_ID } from "../templates/pickAndPlace";

import { PALETTE_GROUPS } from "../../components/behavior-editor/paletteGroups.js";

export type BehaviorPaletteItem = {
  key: string;
  label: string;
  help?: string;
  paletteRowId?: string;
  insertOverrides?: Partial<BTNode>;
};

export type BehaviorPaletteGroup = {
  key: string;
  title: string;
  items: BehaviorPaletteItem[];
};

type TemplateVisibilityDecision = {
  visibleTemplateIds: Set<string>;
  userHint: string | null;
};

/** Locomotion policy rows for the Motion palette (merged after non-locomotion motion items). */
export const TRAINED_LOCOMOTION_PALETTE_ROWS: Array<{
  paletteRowId: string;
  key: "runTrainedModel";
  label: string;
  help: string;
  insertOverrides: Pick<Extract<BTNode, { kind: "runTrainedModel" }>, "modelId"> & {
    label?: string;
  };
}> = [
  {
    paletteRowId: "runTrainedModel_go2_walk",
    key: "runTrainedModel",
    label: "Go2 Walk",
    help: "Runs the built-in Unitree Go2 walking model.",
    insertOverrides: { modelId: "go2-walk-stagef", label: "Go2 Walk" },
  },
  {
    paletteRowId: "runTrainedModel_spot_walk",
    key: "runTrainedModel",
    label: "Spot Walk",
    help: "Runs the built-in Boston Dynamics Spot walking model.",
    insertOverrides: { modelId: "spot-walk-locomotion", label: "Spot Walk" },
  },
];

const PICK_PLACE_ROOT_LABEL = "Pick & place flow";

/** UR5e (Menagerie or Critter) + Robotiq 2F-85: slightly shorter descend so grasp gates line up with typical hover offsets. */
const UR5E_ROBOTIQ_PICK_PLACE_TUNING = {
  approachY: 0.14,
  descendDy: -0.095,
} as const;

function isPickPlaceFlowDoc(doc: BehaviorDocument): boolean {
  const root = doc.nodes[doc.rootId];
  if (!root || root.kind !== "sequence") return false;
  return root.label === PICK_PLACE_ROOT_LABEL;
}

function shouldApplyPickPlaceRules(ctx: SceneBehaviorContext): boolean {
  if (ctx.scenarioId === "pick_and_place") return true;
  return Boolean(ctx.cubeBodyUuid || ctx.trayBodyUuid);
}

function ur5eRobotiqTuning(ctx: SceneBehaviorContext) {
  const ur5eArm =
    ctx.primaryRobotLibraryId === "ur5e" ||
    ctx.primaryRobotLibraryId === "ur5e_critter" ||
    ctx.recipeRobotId === "ur5e" ||
    ctx.recipeRobotId === "ur5e_critter";
  if (!ur5eArm || ctx.gripperLibraryId !== "robotiq_2f85") return null;
  return UR5E_ROBOTIQ_PICK_PLACE_TUNING;
}

function patchPickPlaceFromContext(
  doc: BehaviorDocument,
  ctx: SceneBehaviorContext
): BehaviorDocument {
  if (!isPickPlaceFlowDoc(doc) || !shouldApplyPickPlaceRules(ctx)) return doc;

  const tuning = ur5eRobotiqTuning(ctx);
  const nextNodes: Record<string, BTNode> = { ...doc.nodes };

  const patchNode = (id: string, patch: Partial<BTNode>) => {
    const cur = nextNodes[id];
    if (!cur) return;
    nextNodes[id] = { ...cur, ...patch } as BTNode;
  };

  for (const node of Object.values(nextNodes)) {
    const label = typeof node.label === "string" ? node.label : "";

    if (node.kind === "randomizeObjectPose" && ctx.cubeBodyUuid) {
      patchNode(node.id, { bodyUuid: ctx.cubeBodyUuid });
    }

    if (node.kind === "conditionObjectGrasped" && ctx.cubeBodyUuid) {
      if (label.includes("carry") || label.includes("place") || label.includes("grasped")) {
        patchNode(node.id, { bodyUuid: ctx.cubeBodyUuid });
      }
    }

    if (node.kind === "moveEeToXyz") {
      if (label.includes("pick") && ctx.cubeBodyUuid) {
        patchNode(node.id, {
          targetBodyUuid: ctx.cubeBodyUuid,
          ...(tuning ? { y: tuning.approachY } : {}),
        });
      }
      if (label.includes("drop") && ctx.trayBodyUuid) {
        patchNode(node.id, {
          targetBodyUuid: ctx.trayBodyUuid,
          ...(tuning ? { y: tuning.approachY } : {}),
        });
      }
    }

    if (node.kind === "conditionEeNear") {
      if (label.includes("hovering above pick") && ctx.cubeBodyUuid) {
        patchNode(node.id, {
          targetBodyUuid: ctx.cubeBodyUuid,
          ...(tuning ? { y: tuning.approachY } : {}),
        });
      }
      if (label.includes("hovering above drop") && ctx.trayBodyUuid) {
        patchNode(node.id, {
          targetBodyUuid: ctx.trayBodyUuid,
          ...(tuning ? { y: tuning.approachY } : {}),
        });
      }
      if (label.includes("at grasp point") && ctx.cubeBodyUuid) {
        patchNode(node.id, {
          targetBodyUuid: ctx.cubeBodyUuid,
          ...(tuning ? { y: tuning.approachY + tuning.descendDy } : {}),
        });
      }
    }

    if (node.kind === "moveEeByOffset" && tuning && label.includes("Descend")) {
      patchNode(node.id, { dy: tuning.descendDy });
    }
  }

  return { ...doc, nodes: nextNodes };
}

function computeTemplateVisibility(ctx: SceneBehaviorContext): TemplateVisibilityDecision {
  const visibleTemplateIds = new Set<string>();

  if (ctx.isSceneEmpty) {
    visibleTemplateIds.add(PICK_PLACE_TEMPLATE_ID);
    visibleTemplateIds.add(GO2_WALK_TEMPLATE_ID);
    visibleTemplateIds.add(SPOT_WALK_TEMPLATE_ID);
    return {
      visibleTemplateIds,
      userHint: "Load a robot into the scene to unlock robot-specific behavior templates.",
    };
  }

  if (ctx.hasManipulationGripper) {
    visibleTemplateIds.add(PICK_PLACE_TEMPLATE_ID);
  }
  if (ctx.supportsLocoPolicyContract && ctx.locomotionRobotLibraryId === "unitree_go2") {
    visibleTemplateIds.add(GO2_WALK_TEMPLATE_ID);
  }
  if (ctx.supportsLocoPolicyContract && ctx.locomotionRobotLibraryId === "boston_dynamics_spot") {
    visibleTemplateIds.add(SPOT_WALK_TEMPLATE_ID);
  }

  return { visibleTemplateIds, userHint: null };
}

export function getVisibleBehaviorDocumentsForContext(behaviors: BehaviorDocument[]): {
  visibleBehaviors: BehaviorDocument[];
  userHint: string | null;
} {
  const ctx = buildSceneBehaviorContext();
  const decision = computeTemplateVisibility(ctx);
  const visibleBehaviors = behaviors.filter((doc) => {
    if (!doc?.isTemplate) return true;
    return decision.visibleTemplateIds.has(doc.id);
  });
  return {
    visibleBehaviors,
    userHint: decision.userHint,
  };
}

export function getBehaviorTemplateAvailabilityForContext(behaviors: BehaviorDocument[]): {
  unavailableTemplateIds: Set<string>;
  userHint: string | null;
} {
  const ctx = buildSceneBehaviorContext();
  const decision = computeTemplateVisibility(ctx);
  if (ctx.isSceneEmpty) {
    return { unavailableTemplateIds: new Set<string>(), userHint: decision.userHint };
  }
  const unavailableTemplateIds = new Set<string>();
  for (const doc of behaviors) {
    if (!doc?.isTemplate) continue;
    if (!decision.visibleTemplateIds.has(doc.id)) {
      unavailableTemplateIds.add(doc.id);
    }
  }
  return { unavailableTemplateIds, userHint: decision.userHint };
}

/**
 * Default behavior to show when opening the editor or after deleting the current one:
 * first built-in template that is compatible with the current scene (same order as the
 * behavior list: templates before user docs, then by name), otherwise first user behavior.
 */
export function getDefaultSelectedBehaviorId(
  behaviors: Record<string, BehaviorDocument>
): string | null {
  const list = Object.values(behaviors).filter(Boolean);
  if (list.length === 0) return null;
  const ctx = buildSceneBehaviorContext();
  const { visibleTemplateIds } = computeTemplateVisibility(ctx);
  const sorted = [...list].sort((a, b) => {
    const at = a.isTemplate ? 0 : 1;
    const bt = b.isTemplate ? 0 : 1;
    if (at !== bt) return at - bt;
    return (a.name || "").localeCompare(b.name || "");
  });
  const firstCompatTemplate = sorted.find(
    (d) => d.isTemplate === true && visibleTemplateIds.has(d.id)
  );
  if (firstCompatTemplate) return firstCompatTemplate.id;
  const firstUser = sorted.find((d) => d.isTemplate !== true);
  if (firstUser) return firstUser.id;
  return null;
}

export function filterPaletteGroupsForContext(
  baseGroups: BehaviorPaletteGroup[],
  ctx: SceneBehaviorContext
): BehaviorPaletteGroup[] {
  const locomotionItems =
    ctx.supportsLocoPolicyContract && ctx.locomotionRobotLibraryId === "unitree_go2"
      ? TRAINED_LOCOMOTION_PALETTE_ROWS.filter(
          (row) => row.insertOverrides.modelId === "go2-walk-stagef"
        )
      : ctx.supportsLocoPolicyContract && ctx.locomotionRobotLibraryId === "boston_dynamics_spot"
        ? TRAINED_LOCOMOTION_PALETTE_ROWS.filter(
            (row) => row.insertOverrides.modelId === "spot-walk-locomotion"
          )
        : [];

  return baseGroups.map((g: BehaviorPaletteGroup) => {
    if (g.key !== "motion") return g;
    const withoutLocomotion = g.items.filter((it) => it.key !== "runTrainedModel");
    return {
      ...g,
      items: [...withoutLocomotion, ...locomotionItems],
    };
  });
}

export function getPaletteGroupsForBehaviorEditor(): BehaviorPaletteGroup[] {
  const ctx = buildSceneBehaviorContext();
  if (ctx.isSceneEmpty) {
    const allLocomotion = [...TRAINED_LOCOMOTION_PALETTE_ROWS];
    return (PALETTE_GROUPS as BehaviorPaletteGroup[]).map((g: BehaviorPaletteGroup) => {
      if (g.key !== "motion") return g;
      const withoutLocomotion = g.items.filter((it) => it.key !== "runTrainedModel");
      return {
        ...g,
        items: [...withoutLocomotion, ...allLocomotion],
      };
    });
  }
  return filterPaletteGroupsForContext(PALETTE_GROUPS as BehaviorPaletteGroup[], ctx);
}

export function applyBehaviorDocumentSceneRules(doc: BehaviorDocument): BehaviorDocument {
  const ctx = buildSceneBehaviorContext();
  return patchPickPlaceFromContext(doc, ctx);
}
